// src/hooks/usePushNotifications.js
import { useState, useEffect, useCallback } from "react";
import { fetchWithAuth, postWithAuth, deleteWithAuth } from "../utils/fetchWithAuth";

// Support both Vite and CRA environment variables
const VAPID_PUBLIC_KEY = 
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_VAPID_PUBLIC_KEY) ||
  (typeof process !== 'undefined' && process.env?.REACT_APP_VAPID_PUBLIC_KEY) ||
  null;

/**
 * Convert a base64 string to Uint8Array (needed for applicationServerKey)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

/**
 * Hook to manage push notification subscriptions
 */
export function usePushNotifications() {
  const [permission, setPermission] = useState("default");
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check support and current state on mount
  useEffect(() => {
    const supported = "serviceWorker" in navigator && "PushManager" in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      
      // Check for an existing subscription. getRegistration, not .ready:
      // .ready never resolves when no worker is registered yet.
      navigator.serviceWorker.getRegistration().then((registration) => {
        registration?.pushManager.getSubscription().then((sub) => setSubscription(sub));
      }).catch(() => {});
    }
  }, []);

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      return { success: false, error: "Push notifications not supported" };
    }

    setLoading(true);

    try {
      // The build-time key if there is one, otherwise the server's. The
      // production build never had VITE_VAPID_PUBLIC_KEY.
      let publicKey = VAPID_PUBLIC_KEY;
      if (!publicKey) {
        const info = await fetchWithAuth("/api/push/subscribe/");
        publicKey = info?.public_key || "";
      }
      if (!publicKey) {
        return { success: false, error: "Push notifications not configured" };
      }

      // Request notification permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== "granted") {
        return { success: false, error: "Permission denied" };
      }

      // Register service worker if not already registered
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register("/sw.js");
      }
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      // Send subscription to backend using fetchWithAuth
      await postWithAuth("/api/push/subscribe/", {
        subscription: sub.toJSON(),
      });

      setSubscription(sub);
      return { success: true };
    } catch (err) {
      console.error("Push subscription failed:", err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async () => {
    if (!subscription) {
      return { success: true };
    }

    setLoading(true);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();
      
      // Remove from backend using fetchWithAuth
      await fetchWithAuth("/api/push/subscribe/", {
        method: "DELETE",
        json: { endpoint: subscription.endpoint },
      });

      setSubscription(null);
      return { success: true };
    } catch (err) {
      console.error("Push unsubscription failed:", err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [subscription]);

  /**
   * Check if user has an active subscription on the server
   */
  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const data = await fetchWithAuth("/api/push/subscribe/");
      return data.has_subscription || false;
    } catch {
      return false;
    }
  }, []);

  return {
    permission,
    subscription,
    isSupported,
    loading,
    subscribe,
    unsubscribe,
    checkSubscriptionStatus,
  };
}

export default usePushNotifications;